<template>
  <div style="width: 100%" class="fill-height">
    <v-container>
      <v-row class="justify-center">
        <v-col cols="12" sm="11" lg="11" :class="['px-0']">

          <!-- Sticky Filter Header -->
          <div class="table-header-filters">
            <v-row dense class="align-center">
              <!-- Location selector (hidden if only one location) -->
              <v-col v-if="locations.length > 1" cols="12" sm="6" md="3" lg="2">
                <v-autocomplete
                  v-model="selectedLocationId"
                  :items="locations"
                  item-text="name"
                  item-value="id"
                  label="Prodajno mjesto"
                  dense outlined hide-details
                  class="filter-select"
                  @change="onLocationChange">
                  <template #prepend-inner>
                    <v-icon color="#3989C9" small>mdi-store</v-icon>
                  </template>
                </v-autocomplete>
              </v-col>

              <!-- Item name search -->
              <v-col cols="12" sm="6" md="3" lg="2">
                <v-text-field
                  v-model="searchTerm"
                  label="Pretraži po nazivu artikla"
                  dense outlined hide-details clearable
                  class="filter-select"
                  @input="onSearchInput">
                  <template #append>
                    <v-icon color="#3989C9" small>mdi-magnify</v-icon>
                  </template>
                </v-text-field>
              </v-col>

              <!-- KPD code search -->
              <v-col cols="12" sm="6" md="2" lg="2">
                <v-text-field
                  v-model="searchKpdCode"
                  label="Pretraži po KPD šifri"
                  dense outlined hide-details clearable
                  class="filter-select"
                  @input="onSearchInput">
                </v-text-field>
              </v-col>

              <!-- Category filter -->
              <v-col cols="12" sm="6" md="2" lg="2">
                <v-autocomplete
                  v-model="selectedCategories"
                  :items="categories"
                  item-text="name"
                  item-value="id"
                  label="Kategorije"
                  multiple dense outlined hide-details
                  small-chips
                  class="filter-select"
                  @change="loadItems(true)">
                </v-autocomplete>
              </v-col>

              <!-- Has/No KPD filter -->
              <v-col cols="12" sm="6" md="2" lg="2">
                <v-select
                  v-model="hasKpdCode"
                  :items="kpdFilterOptions"
                  item-text="label"
                  item-value="value"
                  label="KPD Status"
                  dense outlined hide-details
                  class="filter-select"
                  @change="loadItems(true)">
                </v-select>
              </v-col>

              <!-- Bulk update button -->
              <v-col cols="12" sm="6" md="2" lg="2">
                <v-btn
                  class="search-btn"
                  @click="openBulkUpdateDialog"
                  elevation="0" small block
                  :disabled="!selectedLocationId">
                  <v-icon small class="mr-1">mdi-playlist-edit</v-icon>
                  Bulk Update
                </v-btn>
              </v-col>
            </v-row>
          </div>

          <!-- Loading -->
          <div v-if="isLoading" class="loading-overlay-receipts">
            <v-progress-circular indeterminate size="64"></v-progress-circular>
          </div>

          <!-- Empty State -->
          <div v-if="!isLoading && items.length === 0" class="text-center pa-8">
            <v-icon size="64" color="grey lighten-1">mdi-package-variant</v-icon>
            <div class="text-h6 grey--text mt-2">Nema artikala za prikaz</div>
            <div class="grey--text caption">Pokušajte promijeniti filtere</div>
          </div>

          <!-- Items Cards -->
          <v-card v-for="item in items" :key="item.id" hover class="item-card">
            <v-row class="ma-0 item-row" style="min-height: 60px !important; padding: 12px 16px;">

              <!-- Image (if exists) -->
              <div v-if="item.picture" style="width: 6% !important; display: flex; align-items: center; justify-content: center;">
                <v-img :src="item.picture" contain height="48" style="border-radius: 8px;"></v-img>
              </div>

              <!-- Name & Code -->
              <v-col cols="5" sm="5" md="3" lg="3" style="min-width: 100px; max-width: 100%; padding-bottom: 10px !important;" class="flex-grow-1">
                <div class="caption grey--text">Naziv</div>
                <div class="one-liner-text item-name">{{ item.name }}</div>
                <div v-if="item.code" class="caption grey--text mt-1">{{ item.code }}</div>
              </v-col>

              <!-- Categories -->
              <v-col cols="3" sm="3" md="2" lg="2">
                <div class="caption grey--text">Kategorije</div>
                <div>
                  <v-chip
                    v-for="category in item.categories"
                    :key="category.id"
                    x-small
                    class="mr-1 mb-1"
                    color="#3989C9"
                    text-color="white">
                    {{ category.name }}
                  </v-chip>
                  <span v-if="!item.categories || !item.categories.length" class="grey--text caption">-</span>
                </div>
              </v-col>

              <!-- KPD Code - INLINE EDITING -->
              <v-col cols="4" sm="4" md="3" lg="3">
                <div class="caption grey--text">KPD Šifra</div>
                <!-- Display Mode -->
                <div v-if="editingItemId !== item.id">
                  <v-chip
                    small
                    :color="item.kpdcode ? '#00B3BE' : 'grey'"
                    text-color="white"
                    @click="startEditKpd(item)"
                    class="kpd-chip"
                    style="cursor: pointer;">
                    {{ item.kpdcode || 'Dodaj KPD' }}
                    <v-icon small right>mdi-pencil</v-icon>
                  </v-chip>
                </div>
                <!-- Edit Mode -->
                <div v-else class="d-flex align-center">
                  <v-text-field
                    v-model="editingKpdValue"
                    dense outlined hide-details
                    autofocus
                    style="max-width: 150px;"
                    @keydown.enter="saveKpdCode(item)"
                    @keydown.esc="cancelEditKpd"
                  />
                  <v-btn icon small @click="saveKpdCode(item)" color="success" class="ml-1">
                    <v-icon small>mdi-check</v-icon>
                  </v-btn>
                  <v-btn icon small @click="cancelEditKpd" color="error">
                    <v-icon small>mdi-close</v-icon>
                  </v-btn>
                </div>
              </v-col>

              <!-- Actions -->
              <v-col cols="2" md="2" lg="2">
                <div class="caption grey--text text-center">Akcije</div>
                <div class="d-flex align-start justify-center">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        v-on="on"
                        v-bind="attrs"
                        icon
                        small
                        color="#3989C9"
                        @click="startEditKpd(item)">
                        <v-icon small>mdi-pencil</v-icon>
                      </v-btn>
                    </template>
                    <span>Uredi KPD šifru</span>
                  </v-tooltip>
                </div>
              </v-col>

            </v-row>
          </v-card>

          <!-- Loading indicator when loading more -->
          <v-row v-if="isLoading && items.length > 0" class="justify-center mt-4 mb-4">
            <v-progress-circular
              indeterminate
              size="48"
              color="#3989C9"
              width="3"
            ></v-progress-circular>
          </v-row>

          <!-- Load More Button -->
          <v-row v-if="hasMore && !isLoading" class="justify-center mt-4 mb-4">
            <v-btn
              class="page-nav-btn-header"
              @click="loadMore"
              :disabled="isLoading"
              elevation="2"
            >
              Učitaj više
              <v-icon small class="ml-2">mdi-chevron-down</v-icon>
            </v-btn>
          </v-row>

          <!-- End of list indicator -->
          <v-row v-if="!hasMore && items.length > 0" class="justify-center mt-4 mb-4">
            <v-col cols="12" class="text-center">
              <div class="caption grey--text">Nema više artikala</div>
            </v-col>
          </v-row>

        </v-col>
      </v-row>
    </v-container>

    <!-- Bulk Update Dialog -->
    <BulkUpdateDialog
      ref="bulkDialog"
      :categories="categories"
      @updated="refreshItems"
    />

    <!-- Snackbars for notifications -->
    <v-snackbar v-model="errorSnackbar" color="error" :timeout="6000" top>
      {{ errorMessage }}
      <template #action="{ attrs }">
        <v-btn text v-bind="attrs" @click="errorSnackbar = false">
          Zatvori
        </v-btn>
      </template>
    </v-snackbar>

    <v-snackbar v-model="successSnackbar" color="success" :timeout="4000" top>
      {{ successMessage }}
    </v-snackbar>

    <v-snackbar v-model="warningSnackbar" color="warning" :timeout="5000" top>
      {{ warningMessage }}
    </v-snackbar>
  </div>
</template>

<script>
import { kpdSifraApi } from '@/api/kpdSifra'
import state from '@/state'
import BulkUpdateDialog from './BulkUpdateDialog.vue'
import { df, auth } from '@/plugins/firebase'

export default {
  name: 'KpdSifra',
  components: { BulkUpdateDialog },

  data () {
    return {
      items: [],
      categories: [],
      locations: [],
      selectedLocationId: null,
      searchTerm: '',
      searchKpdCode: '',
      selectedCategories: [],
      hasKpdCode: null,
      kpdFilterOptions: [
        { label: 'Svi artikli', value: null },
        { label: 'Ima KPD šifru', value: true },
        { label: 'Nema KPD šifru', value: false }
      ],
      isLoading: false,
      pageSize: 20,
      hasMore: false,
      startAfter: null,
      lastDocId: null,

      // Inline editing
      editingItemId: null,
      editingKpdValue: '',

      searchDebounce: null,

      // Snackbars
      errorSnackbar: false,
      errorMessage: '',
      successSnackbar: false,
      successMessage: '',
      warningSnackbar: false,
      warningMessage: ''
    }
  },

  mounted () {
    this.loadLocations()
    this.loadCategories()
  },

  methods: {
    showError (message) {
      this.errorMessage = message
      this.errorSnackbar = true
    },

    showSuccess (message) {
      this.successMessage = message
      this.successSnackbar = true
    },

    showWarning (message) {
      this.warningMessage = message
      this.warningSnackbar = true
    },

    async loadLocations () {
      try {
        const companyId = state.getCurrentCompany().id
        const userId = auth.currentUser.uid

        // Load user locations for this company
        const userLocationsDoc = await df.collection('user_locations').doc(`${userId}.${companyId}`).get()

        if (userLocationsDoc.exists) {
          const data = userLocationsDoc.data()
          const locationsData = data.locations || {}

          // Convert to array, filter active locations, and sort alphabetically
          this.locations = Object.keys(locationsData)
            .map(id => ({
              id: id,
              name: locationsData[id].name || id,
              status: locationsData[id].status,
              resource_status: locationsData[id].resource_status
            }))
            .filter(loc => loc.status === 'OK' && loc.resource_status === 'ACTIVE')
            .sort((a, b) => a.name.localeCompare(b.name))

          // Auto-select location based on current path or if only one exists
          this.autoSelectLocation()
        }
      } catch (error) {
        console.error('Error loading locations:', error)
        this.showError('Greška pri učitavanju prodajnih mjesta')
      }
    },

    autoSelectLocation () {
      // Check if we're on a path with locationId (point-of-sale or cash-register)
      const routeLocationId = this.$route.params.locationId

      if (routeLocationId && this.locations.find(loc => loc.id === routeLocationId)) {
        // We're on a location path, use that location
        this.selectedLocationId = routeLocationId
      } else if (state.getPointOfSale() && state.getPointOfSale().id) {
        // Check if point of sale is set in state
        const posId = state.getPointOfSale().id
        if (this.locations.find(loc => loc.id === posId)) {
          this.selectedLocationId = posId
        }
      } else if (this.locations.length === 1) {
        // Only one location, select it by default
        this.selectedLocationId = this.locations[0].id
      }

      // Load items if location is selected
      if (this.selectedLocationId) {
        this.loadItems(true)
      }
    },

    onLocationChange () {
      // Reset and reload items when location changes
      this.loadItems(true)
    },

    loadCategories () {
      const company = state.getCurrentCompany()
      if (!company || !company.id) {
        console.error('No company found in state!')
        return
      }

      const companyId = company.id
      df.doc(`company_categories/${companyId}`).onSnapshot((doc) => {
        if (doc && doc.data() && doc.data().categories && Object.keys(doc.data().categories).length > 0) {
          const categoriesObj = doc.data().categories
          this.categories = Object.keys(categoriesObj)
            .map(key => categoriesObj[key])
            .filter(cat => cat.status === 'OK')
            .sort((a, b) => a.name.localeCompare(b.name))
        } else {
          this.categories = []
        }
      }, (error) => {
        console.error('Error loading categories:', error)
        this.showError('Greška pri učitavanju kategorija')
        this.categories = []
      })
    },

    async loadItems (reset = false) {
      // Don't load if no location is selected
      if (!this.selectedLocationId) {
        this.items = []
        return
      }

      if (reset) {
        this.items = []
        this.startAfter = null
        this.lastDocId = null
        this.hasMore = false
      }

      this.isLoading = true
      try {
        const params = {
          companyId: state.getCurrentCompany().id,
          locationId: this.selectedLocationId,
          limit: this.pageSize,
          searchTerm: this.searchTerm,
          searchKpd: this.searchKpdCode,
          categories: this.selectedCategories,
          hasKpdCode: this.hasKpdCode
        }

        // Add cursor for pagination (not on first load/reset)
        if (!reset && this.startAfter) {
          params.startAfter = this.startAfter
        }

        const response = await kpdSifraApi.fetchItems(params)

        if (reset) {
          this.items = response.items
        } else {
          this.items.push(...response.items)
        }

        this.hasMore = response.has_more
        this.lastDocId = response.last_doc_id
      } catch (error) {
        console.error('Error loading items:', error)
        this.showError(error.parsedMessage || 'Greška pri učitavanju artikala')
      } finally {
        this.isLoading = false
      }
    },

    async loadMore () {
      if (!this.hasMore || this.isLoading || !this.lastDocId) return

      const currentItemCount = this.items.length

      // Set cursor to last document ID
      this.startAfter = this.lastDocId

      await this.loadItems(false)

      // Scroll to the last item before new items (so it appears just below header)
      this.$nextTick(() => {
        const itemCards = document.querySelectorAll('.item-card')
        if (itemCards[currentItemCount - 1]) {
          // Scroll with offset to account for sticky header (64px header + some padding)
          const element = itemCards[currentItemCount - 1]
          const yOffset = -70 // Negative offset to position below header
          const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset
          window.scrollTo({ top: y, behavior: 'smooth' })
        }
      })
    },

    onSearchInput () {
      clearTimeout(this.searchDebounce)
      this.searchDebounce = setTimeout(() => {
        this.loadItems(true)
      }, 500)
    },

    refreshItems () {
      this.loadItems(true)
    },

    // Inline editing methods
    startEditKpd (item) {
      this.editingItemId = item.id
      this.editingKpdValue = item.kpdcode || ''
    },

    async saveKpdCode (item) {
      try {
        await kpdSifraApi.updateKpdCode(
          state.getCurrentCompany().id,
          this.selectedLocationId,
          item.id,
          this.editingKpdValue.trim()
        )

        // Optimistic update
        item.kpdcode = this.editingKpdValue.trim()
        this.cancelEditKpd()

        this.showSuccess('KPD šifra uspješno ažurirana')
      } catch (error) {
        console.error('Error saving KPD code:', error)
        this.showError(error.parsedMessage || 'Greška pri spremanju KPD šifre')
      }
    },

    cancelEditKpd () {
      this.editingItemId = null
      this.editingKpdValue = ''
    },

    openBulkUpdateDialog () {
      if (!this.selectedLocationId) {
        this.showWarning('Molimo odaberite prodajno mjesto')
        return
      }
      this.$refs.bulkDialog.open(this.selectedLocationId)
    }
  }
}
</script>

<style scoped>
/* Items Cards */
.item-card {
  margin-bottom: 12px !important;
  border-radius: 12px !important;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  border: 1px solid rgba(0, 179, 190, 0.2) !important;
  box-shadow: 0 4px 16px rgba(0, 179, 190, 0.08);
}

.item-card:hover {
  box-shadow: 0 8px 24px rgba(0, 179, 190, 0.15) !important;
  transform: translateY(-2px);
}

.item-row {
  padding: 12px 16px !important;
  min-height: 60px !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  background: rgba(255, 255, 255, 0.95);
}

.item-name {
  font-weight: 600 !important;
  font-size: 14px !important;
}

.one-liner-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.kpd-chip {
  cursor: pointer;
  transition: all 0.2s ease;
}

.kpd-chip:hover {
  opacity: 0.8;
}

.table-header-filters {
  position: -webkit-sticky;
  position: sticky;
  top: 64px;
  z-index: 1;
  background: linear-gradient(135deg,
    rgba(0, 179, 190, 0.08) 0%,
    rgba(57, 137, 201, 0.06) 50%,
    rgba(97, 84, 164, 0.08) 100%);
  backdrop-filter: blur(20px);
  padding: 16px;
  border-radius: 12px;
  margin-bottom: 16px;
  border: 1px solid rgba(0, 179, 190, 0.15);
}

.loading-overlay-receipts {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
}

/* Filter Select Inputs */
.filter-select.v-text-field--outlined >>> fieldset {
  border-color: rgba(57, 137, 201, 0.2) !important;
  border-radius: 8px !important;
}

.filter-select.v-text-field--outlined:hover >>> fieldset {
  border-color: rgba(57, 137, 201, 0.4) !important;
}

.filter-select.v-text-field--outlined.v-input--is-focused >>> fieldset {
  border-color: #3989C9 !important;
  border-width: 2px !important;
}

.filter-select >>> .v-input__slot {
  background: rgba(255, 255, 255, 0.8) !important;
  backdrop-filter: blur(10px) !important;
  min-height: 36px !important;
  border-radius: 8px !important;
}

.filter-select.v-text-field--outlined >>> .v-input__control {
  border-radius: 8px !important;
}

.filter-select >>> .v-text-field__slot input {
  font-size: 13px !important;
  font-weight: 500 !important;
  color: rgba(70, 70, 80, 0.9) !important;
  letter-spacing: 0.3px !important;
}

.filter-select >>> label {
  font-size: 12px !important;
  font-weight: 500 !important;
  color: rgba(57, 137, 201, 0.8) !important;
}

.filter-select >>> .v-select__selection,
.filter-select >>> .v-text-field__slot input {
  font-size: 13px !important;
  font-weight: 500 !important;
  color: rgba(70, 70, 80, 0.9) !important;
  letter-spacing: 0.3px !important;
}

/* Filter Select Dropdown Menu */
.filter-select >>> .v-menu__content {
  background: rgba(255, 255, 255, 0.98) !important;
  backdrop-filter: blur(20px) !important;
  border-radius: 8px !important;
  border: 1px solid rgba(0, 179, 190, 0.15) !important;
  box-shadow: 0 8px 24px rgba(0, 179, 190, 0.12) !important;
  max-height: 300px !important;
}

.filter-select >>> .v-list-item {
  font-size: 13px !important;
  font-weight: 500 !important;
  transition: all 0.2s ease !important;
  min-height: 40px !important;
}

.filter-select >>> .v-list-item:hover {
  background: rgba(57, 137, 201, 0.08) !important;
}

.filter-select >>> .v-list-item--active {
  background: rgba(0, 179, 190, 0.12) !important;
  color: #3989C9 !important;
}

.filter-select >>> .v-simple-checkbox {
  color: #3989C9 !important;
}

/* Search Button */
.search-btn {
  background: rgba(255, 255, 255, 0.95) !important;
  color: #3989C9 !important;
  border: 1px solid rgba(57, 137, 201, 0.25) !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08) !important;
  backdrop-filter: blur(10px) !important;
  border-radius: 8px !important;
  transition: all 0.2s ease !important;
  padding: 0 20px !important;
  height: 36px !important;
  font-weight: 500 !important;
  text-transform: none !important;
  font-size: 13px !important;
  letter-spacing: 0.3px !important;
}

.search-btn:hover {
  background: rgba(255, 255, 255, 1) !important;
  border-color: #3989C9 !important;
  box-shadow: 0 2px 6px rgba(57, 137, 201, 0.2) !important;
  transform: translateY(-1px);
}

.search-btn .v-icon {
  color: #3989C9 !important;
  font-size: 18px !important;
}

/* Load More Button */
.page-nav-btn-header {
  background: rgba(255, 255, 255, 0.95) !important;
  color: #3989C9 !important;
  border: 1px solid rgba(57, 137, 201, 0.25) !important;
  border-radius: 8px !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08) !important;
  backdrop-filter: blur(10px) !important;
  transition: all 0.2s ease !important;
  text-transform: none !important;
  font-weight: 500 !important;
  font-size: 13px !important;
  letter-spacing: 0.3px !important;
  padding: 0 16px !important;
  height: 36px !important;
  min-width: 110px !important;
}

.page-nav-btn-header:hover:not(:disabled) {
  background: rgba(255, 255, 255, 1) !important;
  border-color: #3989C9 !important;
  box-shadow: 0 2px 6px rgba(57, 137, 201, 0.2) !important;
  transform: translateY(-1px);
}

.page-nav-btn-header:disabled {
  opacity: 0.5 !important;
  cursor: not-allowed !important;
  background: rgba(245, 245, 245, 0.8) !important;
  border-color: rgba(200, 200, 200, 0.3) !important;
  color: rgba(158, 158, 158, 0.6) !important;
}
</style>
